import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Avatar } from '@swyftx/aviary/atoms/Avatar';
import { Button } from '@swyftx/aviary/atoms/Button';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Separator } from '@swyftx/aviary/atoms/Seperator/Seperator';
import { Body } from '@swyftx/aviary/atoms/Typography';
import { Document, DownloadDocument, LogOut } from '@swyftx/aviary/icons/outlined';
import { EnhancedTooltip } from '@swyftx/aviary/molecules/EnhancedTooltip/EnhancedTooltip';
import { Menu, MenuItem } from '@swyftx/aviary/molecules/Menu';

import { Links } from '@shared/constants';
import { UserStore } from '@shared/store';
import { cn } from '@shared/utils/lib/ui';

import { useLogout } from '@hooks/Logout';
import { MarginTransactionsReportModal } from '@routes/SwyftxMargin/components/Account';

import { observer } from 'mobx-react-lite';
import { useSwyftxMargin } from 'src/lib/margin';
import { SwitchPlatformMenuItem } from 'src/lib/user-profile/components/SwitchPlatformMenuItem/SwitchPlatformMenuItem';

import { SwyftxMarginAppHeaderProfileDetails } from './SwyftxMarginAppHeaderProfileDetails';

const SwyftxMarginAppHeaderProfile: React.FC = observer(() => {
  const { userProfile } = UserStore.useUserStore;
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const { t } = useTranslation('margin');
  const { isUnderMaintenance } = useSwyftxMargin();
  const logout = useLogout();

  return (
    <Menu
      open={menuOpen}
      align='end'
      side='bottom'
      onOpenChange={setMenuOpen}
      portalClassName='overflow-hidden'
      triggerContent={
        <Button
          id='header-profile'
          data-spotlightelementid='header-profile'
          layout='icon'
          className='ml-4 p-0'
          leadingIcon={
            <Avatar
              text={userProfile?.name.first && userProfile?.name.first[0]}
              size='lg'
              className='h-[2rem] w-[2rem]'
            />
          }
        />
      }
    >
      <SwyftxMarginAppHeaderProfileDetails />
      <SwitchPlatformMenuItem />
      <Separator className='my-8' />
      <MarginTransactionsReportModal>
        <span>
          <EnhancedTooltip
            title={isUnderMaintenance ? t('maintenance.tooltips.transactionReport') : undefined}
            side='left'
          >
            <span className={isUnderMaintenance ? 'cursor-not-allowed' : ''}>
              <FlexLayout
                direction='row'
                spacing={12}
                alignItems='center'
                className={cn(
                  'w-full cursor-pointer select-none rounded-12 p-10 ',
                  isUnderMaintenance ? 'pointer-events-none opacity-30' : 'hover:bg-color-background-surface-hover',
                )}
              >
                <DownloadDocument className='h-24 w-24' />
                <FlexLayout direction='row' alignItems='center' justifyContent='space-between' className='w-full'>
                  <FlexLayout direction='column' alignItems='start' className='text-start'>
                    <Body size='small' weight='emphasis' color='primary'>
                      {t('transactionReports.title')}
                    </Body>
                  </FlexLayout>
                </FlexLayout>
              </FlexLayout>
            </span>
          </EnhancedTooltip>
        </span>
      </MarginTransactionsReportModal>
      <MenuItem
        text='Legal'
        icon={<Document />}
        subMenu={{
          align: 'start',
          side: 'left',
          sideOffset: 8,
          className: 'w-[220px]',
          items: [
            {
              text: t('profile.legal.legalInformation'),
              action: () => window.open(Links.legal.legal, '_blank'),
            },
            {
              text: t('profile.legal.terms'),
              action: () => window.open(Links.legal.tos, '_blank'),
            },
            {
              text: t('profile.legal.privacyPolicy'),
              action: () => window.open(Links.legal.privacyPolicy, '_blank'),
            },
          ],
        }}
      />
      <MenuItem
        text={t('profile.buttons.signOut')}
        color='error'
        className='cursor-pointer'
        action={logout}
        icon={<LogOut className='text-color-text-error' />}
      />
    </Menu>
  );
});

SwyftxMarginAppHeaderProfile.displayName = 'SwyftxMarginAppHeaderProfile';

export { SwyftxMarginAppHeaderProfile };
